import styled from "@emotion/styled";
import assertNever from "assert-never";
import React from "react";
import { HRQuestionId } from "../../../messages";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { AWAITING_APPROVAL_ICON, CHECK_ICON, WARNING_TRIANGLE_ICON } from "../../../utils/svgs_new";
import { AppButton } from "../../Generic/AppButton";
import { AppIcon } from "../../Generic/AppIcon";
import { AppListItem } from "../../Generic/AppList";
import {
  SectionInfoItemData,
  getDocumentQuestionName,
  getQuestionsLeft,
  getSectionTitle,
} from "../utils/onboardingUtils";

interface Props {
  item: SectionInfoItemData;
  language: string;
  onClick: () => void;
  onClickQuestion: (questionId: HRQuestionId) => void;
}

function SectionInfoItem(props: Props) {
  const item = props.item;

  switch (item.type) {
    case "pendingDocument":
      return (
        <SectionDocumentListItem
          title={getDocumentQuestionName({
            language: props.language,
            question: item.question,
          })}
          leading={<AppIcon fill="var(--primary-color)" icon={AWAITING_APPROVAL_ICON} />}
          trailing={
            <span onClick={() => props.onClickQuestion(item.question.id)}>
              {translate(STRINGS.UNDER_REVIEW)}
            </span>
          }
        />
      );

    case "pendingEmailVerification":
      return (
        <SectionDocumentListItem
          title={item.question.questionText}
          leading={<AppIcon fill="var(--primary-color)" icon={AWAITING_APPROVAL_ICON} />}
          trailing={
            <span onClick={() => props.onClickQuestion(item.question.id)}>
              {translate(STRINGS.PENDING_VERIFICATION)}
            </span>
          }
        />
      );

    case "pendingSection":
      return (
        <SectionDocumentListItem
          title={getSectionTitle(item.section, props.language)}
          leading={<AppIcon fill="var(--primary-color)" icon={AWAITING_APPROVAL_ICON} />}
          trailing={
            <AppButton color="black" variant="link" size="extra-small" onClick={props.onClick}>
              {translate(STRINGS.UNDER_REVIEW)}
            </AppButton>
          }
        />
      );

    case "rejectedDocument":
      return (
        <SectionDocumentListItem
          title={getDocumentQuestionName({
            language: props.language,
            question: item.question,
          })}
          leading={<AppIcon stroke="var(--warning)" icon={WARNING_TRIANGLE_ICON} />}
          trailing={
            <AppButton color="danger" variant="link" size="extra-small">
              {translate(STRINGS.FIX)}
            </AppButton>
          }
          onClick={() => props.onClickQuestion(item.question.id)}
        />
      );

    case "rejectedQuestion":
      return (
        <SectionDocumentListItem
          title={item.question.questionText}
          leading={<AppIcon stroke="var(--warning)" icon={WARNING_TRIANGLE_ICON} />}
          trailing={
            <AppButton color="danger" variant="outline" size="extra-small">
              {translate(STRINGS.FIX)}
            </AppButton>
          }
          onClick={() => props.onClickQuestion(item.question.id)}
        />
      );

    case "completedQuestions":
      return (
        <SectionQuestionListItem
          title={`${item.numCompleted}/${item.numTotal} ${translate(STRINGS.COMPLETED)} ${translate(
            item.numCompleted > 1 ? STRINGS.QUESTIONS : STRINGS.QUESTION
          ).toLocaleLowerCase()}`}
          leading={<AppIcon stroke="var(--tw-emerald-600)" icon={CHECK_ICON} />}
        />
      );

    case "buttonStart":
      return (
        <AppListItem
          title={
            <AppButton block={true} color="primary" onClick={props.onClick}>
              {translate(STRINGS.START_FILLING)} (
              {formatSeconds(item.section.data.secondsToComplete ?? 0)})
            </AppButton>
          }
        />
      );

    case "buttonContinue":
      return (
        <AppListItem
          title={
            <AppButton block={true} color="primary" onClick={props.onClick}>
              {translate(STRINGS.CONTINUE)} ({formatQuestionsLeft(getQuestionsLeft(item.section))})
            </AppButton>
          }
        />
      );

    case "buttonReview":
      return (
        <AppListItem
          title={
            <AppButton block={true} color="primary" variant="outline" onClick={props.onClick}>
              {translate(STRINGS.VIEW_ANSWERS)}
            </AppButton>
          }
        />
      );

    default:
      assertNever(item);
  }
}

const SectionDocumentListItem = styled(AppListItem)`
  font-size: 14px;
  font-weight: 600;
  color: #000;
`;

const SectionQuestionListItem = styled(AppListItem)`
  font-size: 14px;
  font-weight: 600;
  color: #000;
`;

function formatSeconds(secondsToComplete: number) {
  return globalize().formatUnit(secondsToComplete / 60, "minute");
}

function formatQuestionsLeft(questionsLeft: number) {
  return `${questionsLeft} ${
    questionsLeft > 1 ? translate(STRINGS.QUESTIONS) : translate(STRINGS.QUESTION)
  } ${translate(STRINGS.LEFT)}`;
}

export default SectionInfoItem;
